import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Grid, FormHelperText } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { resolverFiat } from './resolverFiat'
import { resolverCrypto } from './resolverCrypto'
import { Modal } from '../../../../../../../components/Dashboard/Modal'
import { CURRENCIES, currencyCryptoOrFiat } from '../../../../../../../constants/currencies'
import { InputField } from '../../../../../../../components/Dashboard/InputField'
import { InputSelect } from '../../../../../../../components/Dashboard/InputSelect'
import { useRest, useRefresh, useUserIdSf } from '../../../../../../../components/Context'
import { Loader } from '../../../../../../../components/Loader'

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    width: 710,
    height: 'auto',
    paddingTop: theme.spacing(1),
  },
  input: {
    height: 91,
    '& label': {
      paddingBottom: theme.spacing(0.5),
    },
  },
}))

type Input = {
  currency: string
  description?: string
  beneficiaryName: string
  bankBranchName: string
  bankAddress: string
  bankSwiftBic: string
  bankAccountNumber: string
  referenceToInclude?: string
  intermediaryBankName?: string
  intermediaryBankSwiftBic?: string
  intermediaryBankAddress?: string
  address: string
  suffix: string
}

interface IAddAccount {
  isOpen: boolean
  modalClose: () => void
}

const AddAccountPopup: FC<IAddAccount> = ({ isOpen, modalClose }) => {
  const { retrieveUserIdSf } = useUserIdSf()
  const defaultSelectedCurrency: string = CURRENCIES[0].value
  const classes = useStyles()
  const { rxiosAuth } = useRest()
  const { saveIsRefresh } = useRefresh()
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedCurrency, setSelectedCurrency] = useState<string>(defaultSelectedCurrency)
  const [isBeneficiaryNameUnique, setIsBeneficiaryNameUnique] = useState<boolean>(true)
  const [isBankAccountNumberUnique, setIsBankAccountNumberUnique] = useState<boolean>(true)
  const [currencyType, setCurrencyType] = useState<string>()
  const [isCurrenciesUnique, setCurrenciesUnique] = useState<boolean>(true)

  const formMethods = useForm<Input>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver: currencyType === 'fiat' ? resolverFiat : resolverCrypto,
  })

  const { handleSubmit, reset, setError, trigger, getValues, formState, watch } = formMethods

  const isSubmitDisabled = useMemo(() => {
    return !isBeneficiaryNameUnique || !isBankAccountNumberUnique || !isCurrenciesUnique
  }, [isBankAccountNumberUnique, isBeneficiaryNameUnique, isCurrenciesUnique])

  const beneficiaryNameError = useMemo(() => {
    if (formState?.errors?.beneficiaryName) {
      const { beneficiaryName } = formState.errors
      return !isBeneficiaryNameUnique ? 'Beneficiary name is already used' : beneficiaryName.message
    }
    return ''
  }, [formState, isBeneficiaryNameUnique])

  const bankAccountNumberError = useMemo(() => {
    if (formState?.errors?.bankAccountNumber) {
      const { bankAccountNumber } = formState.errors
      return !isBankAccountNumberUnique ? 'Bank Account Number name is already used' : bankAccountNumber.message
    }
    return ''
  }, [formState, isBankAccountNumberUnique])

  const onValidateBeneficiaryName = useCallback(
    // eslint-disable-next-line
    () => {
      const { beneficiaryName } = getValues()
      rxiosAuth.post(`settlement/unique-beneficiary-name`, { name: beneficiaryName }).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setIsBeneficiaryNameUnique(next.success)
        },
        (error) => {
          toast.error(error.response.data.message)
          setError('beneficiaryName', {
            type: 'manual',
            message: error.response.data.message,
          })
        },
      )
      trigger(['beneficiaryName'])
    },
    [getValues, rxiosAuth, setError, trigger],
  )

  const onValidateBankAccountNumber = useCallback(
    // eslint-disable-next-line
    () => {
      const { bankAccountNumber } = getValues()
      rxiosAuth.post(`settlement/unique-bank-account-number`, { name: bankAccountNumber }).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setIsBankAccountNumberUnique(next.success)
        },
        (error) => {
          toast.error(error.response.data.message)
          setError('bankAccountNumber', {
            type: 'manual',
            message: error.response.data.message,
          })
        },
      )
      trigger(['bankAccountNumber'])
    },
    [getValues, rxiosAuth, setError, trigger],
  )

  const isCurrencyUnique = useCallback(() => {
    const params = {
      currencies: selectedCurrency,
      idSf: retrieveUserIdSf(),
    }
    rxiosAuth.post(`settlement/unique-currencies`, { ...params }).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setCurrenciesUnique(next.success)
        if (!next.success) {
          setError('currency', {
            type: 'manual',
            message: 'The settlement address for this currency already exists',
          })
        }
      },
      (error) => {
        toast.error(error.response.data.message)
        setError('currency', {
          type: 'manual',
          message: error.response.data.message,
        })
      },
    )
    trigger(['currency'])
  }, [retrieveUserIdSf, rxiosAuth, selectedCurrency, setError, trigger])

  useEffect(() => {
    // eslint-disable-next-line
    const subscription = watch((value: any) => {
      if (isOpen) {
        setSelectedCurrency(value.currency)
        setCurrencyType(currencyCryptoOrFiat(value.currency))
      }
    })

    return () => subscription.unsubscribe()
  }, [isOpen, watch])

  const onSubmit = useCallback(
    // eslint-disable-next-line
    async (data: any) => {
      let request = ''
      let postData = {}

      if (currencyType === 'fiat') {
        request = 'settlement/add-account'
        postData = {
          currency: data.currency,
          description: data.description,
          beneficiaryName: data.beneficiaryName,
          bankBranchName: data.bankBranchName,
          bankAddress: data.bankAddress,
          bankSwiftBic: data.bankSwiftBic,
          bankAccountNumber: data.bankAccountNumber,
          referenceToInclude: data.referenceToInclude,
          intermediaryBankName: data.intermediaryBankName,
          intermediaryBankSwiftBic: data.intermediaryBankSwiftBic,
          intermediaryBankAddress: data.intermediaryBankAddress,
        }
      }

      if (currencyType === 'crypto') {
        request = 'settlement/add-account-crypto'
        postData = {
          asset: data.currency,
          address: data.address,
          suffix: data.suffix,
        }
      }
      setLoading(true)
      rxiosAuth.post(request, postData).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setLoading(false)
        },
        // eslint-disable-next-line
        (error) => {
          setLoading(false)
        },
        () => {
          setLoading(false)
          toast.success('Account was added')
          modalClose()
          saveIsRefresh(true)
          formMethods.reset()
          setCurrencyType(currencyCryptoOrFiat(defaultSelectedCurrency))
        },
      )
    },
    [currencyType, rxiosAuth, modalClose, saveIsRefresh, formMethods, defaultSelectedCurrency],
  )

  useEffect(() => {
    formMethods.reset({
      currency: selectedCurrency,
      description: '',
      beneficiaryName: '',
      bankBranchName: '',
      bankAddress: '',
      bankSwiftBic: '',
      bankAccountNumber: '',
      referenceToInclude: '',
      intermediaryBankName: '',
      intermediaryBankSwiftBic: '',
      intermediaryBankAddress: '',
      address: '',
      suffix: '',
    })
  }, [formMethods, selectedCurrency])

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        reset()
        setIsBeneficiaryNameUnique(true)
        setIsBankAccountNumberUnique(true)
        setCurrenciesUnique(true)
        setCurrencyType(currencyCryptoOrFiat(defaultSelectedCurrency))
        setSelectedCurrency(defaultSelectedCurrency)
        setLoading(false)
      }, 300)
    } else {
      isCurrencyUnique()
    }
  }, [defaultSelectedCurrency, isCurrencyUnique, isOpen, reset])

  const handleFormSubmit = useCallback(async () => {
    if (isCurrenciesUnique) {
      await handleSubmit(onSubmit)()
    }
  }, [handleSubmit, onSubmit, isCurrenciesUnique])

  const currenErrorText = useMemo(() => {
    if (!isCurrenciesUnique && !formState?.errors.currency) {
      return <FormHelperText>The settlement address for this currency already exists</FormHelperText>
    }
    return ''
  }, [formState, isCurrenciesUnique])

  return (
    <Modal
      title="Add Account"
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleFormSubmit}
      isSubmit
      isWidthAuto
      isSubmitDisabled={isSubmitDisabled}
    >
      {loading && <Loader />}
      <FormProvider {...formMethods}>
        <Grid container spacing={3} className={classes.body}>
          <Grid item xs={6} className={classes.input}>
            <InputSelect
              inputName="currency"
              labelName={'Select Currency'}
              width={'100%'}
              enums={CURRENCIES}
              defaultValue={defaultSelectedCurrency}
            />
            {currenErrorText}
          </Grid>

          {currencyType === 'fiat' ? (
            <>
              <Grid item xs={6} className={classes.input}>
                <InputField inputName="description" labelName={'Description'} inputType="text" width={'100%'} />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField
                  inputName="beneficiaryName"
                  labelName={'Beneficiary Name'}
                  inputType="text"
                  width={'100%'}
                  onBlurInput={onValidateBeneficiaryName}
                  errorMessageText={beneficiaryNameError}
                />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField inputName="bankBranchName" labelName={'Bank Branch Name'} inputType="text" width={'100%'} />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField inputName="bankAddress" labelName={'Bank Address'} inputType="text" width={'100%'} />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField inputName="bankSwiftBic" labelName={'Bank SWIFT/BIC'} inputType="text" width={'100%'} />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField
                  inputName="bankAccountNumber"
                  labelName={'Bank Account Number'}
                  inputType="text"
                  width={'100%'}
                  onBlurInput={onValidateBankAccountNumber}
                  errorMessageText={bankAccountNumberError}
                />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField
                  inputName="referenceToInclude"
                  labelName={'Reference to Include'}
                  inputType="text"
                  width={'100%'}
                />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField
                  inputName="intermediaryBankName"
                  labelName={'Intermediary Bank Name'}
                  inputType="text"
                  width={'100%'}
                />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField
                  inputName="intermediaryBankSwiftBic"
                  labelName={'Intermediary Bank SWIFT/BIC'}
                  inputType="text"
                  width={'100%'}
                />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField
                  inputName="intermediaryBankAddress"
                  labelName={'Intermediary Bank Address'}
                  inputType="text"
                  width={'100%'}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={6} className={classes.input}>
                <InputField inputName="address" labelName={'Address Value'} inputType="text" width={'100%'} />
              </Grid>

              <Grid item xs={6} className={classes.input}>
                <InputField inputName="suffix" labelName={'Address Suffix'} inputType="text" width={'100%'} />
              </Grid>
            </>
          )}
        </Grid>
      </FormProvider>
    </Modal>
  )
}

export const MemoAddAccountPopup = memo(AddAccountPopup)
